'use client'

// @ts-strict-ignore
import clsx from 'clsx'
import {
	ChangeEvent,
	FocusEvent,
	forwardRef,
	InputHTMLAttributes,
	useId,
	useImperativeHandle,
	useRef,
	useState
} from 'react'

import { SvgCross } from '@/shared/lib/@deprecated/iport-icons/iconsA/SvgCross'

import { LoadingCircle } from '../../LoadingCircle'
import { InputState } from '../types/types'

import cl from './Input.module.scss'

export interface InputProps extends InputState, Omit<InputHTMLAttributes<HTMLInputElement>, 'value'> {
	value?: string | number
	label?: string
	helperText?: string
	onClear?: () => void
	fullWidth?: boolean
	forceFloatLabel?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
	const {
		label,
		helperText,
		fullWidth,
		value,
		onClear,
		forceFloatLabel,

		// state
		isSuccess,
		isLoading,
		isError,

		// html input default props
		className,
		type = 'text',
		disabled,
		onChange,
		onBlur,
		onFocus,
		readOnly,
		...HTMLInputProps
	} = props

	const id = useId()
	const innerRef = useRef<HTMLInputElement>(null)
	useImperativeHandle(ref, () => innerRef.current)

	const [isFocused, setIsFocused] = useState(false)

	const setFocus = () => innerRef.current?.focus()

	const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
		onChange?.(e)
	}

	const onFocusHandler = (e: FocusEvent<HTMLInputElement, Element>) => {
		setIsFocused(true)
		onFocus?.(e)
	}

	const onBlurHandler = (e: FocusEvent<HTMLInputElement, Element>) => {
		setIsFocused(false)
		onBlur?.(e)
	}

	const clearInputValue = () => {
		setFocus()
		onClear?.()
	}

	const isDisabled = disabled || readOnly
	const clearBtnRenderCondition = value?.toString()?.length > 0 && !isDisabled
	const isRegular = !isSuccess && !isLoading && !isError && !isDisabled

	const rootClasses = clsx(cl.root, className, {
		// states
		[cl.root__regular]: isRegular,
		[cl.root__success]: isSuccess,
		[cl.root__loading]: isLoading,
		[cl.root__error]: isError,
		[cl.root__disabled]: isDisabled,
		// styling
		[cl.root__fullWidth]: fullWidth
	})

	const inputClasses = clsx(cl.input, {
		[cl.input__focused]: isFocused
	})

	const labelClasses = clsx(cl.label, {
		[cl.label__focused]: isFocused || value || forceFloatLabel
	})

	const helperTextClasses = clsx(cl.helperText, {
		[cl.helperText__focused]: isFocused
	})

	return (
		<div className={rootClasses} data-class="input">
			{label && (
				<label htmlFor={id} className={labelClasses}>
					{label}
				</label>
			)}

			<div className={inputClasses} onClick={setFocus}>
				<input
					id={id}
					ref={innerRef}
					value={value}
					// events
					onFocus={onFocusHandler}
					onBlur={onBlurHandler}
					onChange={onChangeHandler}
					// default input props
					type={type}
					disabled={disabled}
					readOnly={readOnly}
					{...HTMLInputProps}
				/>
				{clearBtnRenderCondition && onClear && (
					<span className={cl.clearButton} onClick={clearInputValue} tabIndex={-1}>
						<SvgCross />
					</span>
				)}
				{isLoading && <LoadingCircle className={cl.loading} size={12} />}
			</div>

			{helperText && <p className={helperTextClasses}>{helperText}</p>}
		</div>
	)
})
